// Bedienelemente für Mixer und Plugins: Drehregler, Schalter, Auswahl. Ziehen nach oben/unten ändert den Wert,
// ⇧ für fein, Doppelklick setzt auf den Standardwert, Mausrad in kleinen Schritten.
import { useRef } from 'react';
import type { ParamDef } from '../audio/plugins.ts';

/** Wert ↔ 0..1 (logarithmisch für Frequenzen, Zeiten, Ratio) */
export function toNorm(d: Pick<ParamDef, 'min' | 'max' | 'scale'>, v: number) {
  if (d.scale === 'log' && d.min > 0) return Math.log(v / d.min) / Math.log(d.max / d.min);
  return (v - d.min) / (d.max - d.min);
}
export function fromNorm(d: Pick<ParamDef, 'min' | 'max' | 'scale' | 'step'>, n: number) {
  n = Math.max(0, Math.min(1, n));
  let v = d.scale === 'log' && d.min > 0 ? d.min * Math.pow(d.max / d.min, n) : d.min + n * (d.max - d.min);
  const step = d.step ?? (d.scale === 'log' ? 0 : (d.max - d.min) > 50 ? 1 : 0.1);
  if (step) v = Math.round(v / step) * step;
  return Math.max(d.min, Math.min(d.max, Math.round(v * 1000) / 1000));
}

const nf = (v: number, digits: number) => v.toLocaleString('de-DE', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
/** Anzeige eines Parameterwerts */
export function fmtParam(d: ParamDef, v: number): string {
  if (d.choices) return d.choices[Math.round(v)] ?? String(v);
  if (d.bool) return v > 0.5 ? 'an' : 'aus';
  switch (d.unit) {
    case 'Hz': return v >= 1000 ? `${nf(v / 1000, v >= 10000 ? 1 : 2)} kHz` : `${nf(v, 0)} Hz`;
    case 'dB': return `${v > 0 && d.min < 0 ? '+' : v < 0 ? '−' : ''}${nf(Math.abs(v), 1)} dB`;
    case 'ms': return `${nf(v, v < 10 ? 2 : v < 100 ? 1 : 0)} ms`;
    case 's': return `${nf(v, 2)} s`;
    case '%': return `${nf(v, 0)} %`;
    case ':1': return `${nf(v, 1)}:1`;
    default: return nf(v, 2);
  }
}

export function Knob({ d, value, onChange, size = 46, color = 'var(--blue)', label }: { d: ParamDef; value: number; onChange: (v: number) => void; size?: number; color?: string; label?: string }) {
  const drag = useRef<{ y: number; n: number } | null>(null);
  const n = Math.max(0, Math.min(1, toNorm(d, value)));
  const a0 = -135, a = a0 + n * 270, r = size / 2 - 4, c = size / 2;
  const pt = (deg: number) => { const rad = ((deg - 90) * Math.PI) / 180; return [c + r * Math.cos(rad), c + r * Math.sin(rad)]; };
  const arc = (from: number, to: number) => { const [x0, y0] = pt(from), [x1, y1] = pt(to); return `M ${x0} ${y0} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x1} ${y1}`; };
  // bipolare Regler (z. B. Gain −18..+18) zeigen den Bogen von der Mitte aus
  const bip = d.min < 0 && d.max > 0 && d.scale !== 'log';
  const zero = bip ? a0 + toNorm(d, 0) * 270 : a0;
  const [kx, ky] = pt(a);
  return (
    <div className="knob" title={`${d.label}: ${fmtParam(d, value)}. Ziehen, ⇧ für fein, Doppelklick = Standard`}
      onPointerDown={(e) => { drag.current = { y: e.clientY, n }; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); e.preventDefault(); }}
      onPointerMove={(e) => { const g = drag.current; if (!g) return; const dn = ((g.y - e.clientY) / 180) * (e.shiftKey ? 0.15 : 1); onChange(fromNorm(d, g.n + dn)); }}
      onPointerUp={() => { drag.current = null; }}
      onDoubleClick={() => onChange(d.def)}
      onWheel={(e) => { e.preventDefault(); onChange(fromNorm(d, n + (e.deltaY < 0 ? 1 : -1) * (e.shiftKey ? 0.002 : 0.01))); }}>
      <svg width={size} height={size}>
        <path d={arc(a0, a0 + 270)} stroke="#e3e7ee" strokeWidth={4} fill="none" strokeLinecap="round" />
        {Math.abs(a - zero) > 0.5 && <path d={a >= zero ? arc(zero, a) : arc(a, zero)} stroke={color} strokeWidth={4} fill="none" strokeLinecap="round" />}
        <circle cx={c} cy={c} r={r - 7} fill="#fff" stroke="#dfe3ea" />
        <line x1={c + (kx - c) * 0.35} y1={c + (ky - c) * 0.35} x2={c + (kx - c) * 0.72} y2={c + (ky - c) * 0.72} stroke="#071026" strokeWidth={2} strokeLinecap="round" />
      </svg>
      <div className="kv">{fmtParam(d, value)}</div>
      <div className="kl">{label ?? d.label}</div>
    </div>
  );
}

export function Choice({ d, value, onChange }: { d: ParamDef; value: number; onChange: (v: number) => void }) {
  return (
    <div className="choice">
      <div className="kl">{d.label}</div>
      <div className="seg small">{d.choices!.map((c, i) => <button key={c} className={Math.round(value) === i ? 'on' : ''} onClick={() => onChange(i)}>{c}</button>)}</div>
    </div>
  );
}

export function Switch({ d, value, onChange }: { d: ParamDef; value: number; onChange: (v: number) => void }) {
  return <button className={`btn small sw ${value > 0.5 ? 'on' : ''}`} onClick={() => onChange(value > 0.5 ? 0 : 1)}>{d.label}</button>;
}

/** passendes Bedienelement zum Parameter */
export function Control({ d, value, onChange, size }: { d: ParamDef; value: number; onChange: (v: number) => void; size?: number }) {
  if (d.choices) return <Choice d={d} value={value} onChange={onChange} />;
  if (d.bool) return <Switch d={d} value={value} onChange={onChange} />;
  return <Knob d={d} value={value} onChange={onChange} size={size} />;
}

// ---- Fader-Kennlinie wie an einer Konsole: oben fein (um 0 dB), unten gestaucht ----
const TAPER: [number, number][] = [[-96, 0], [-60, 0.06], [-40, 0.17], [-30, 0.27], [-20, 0.4], [-10, 0.57], [-5, 0.7], [0, 0.82], [6, 1]];
export function dbToPos(db: number) {
  if (db <= -96) return 0;
  for (let i = 1; i < TAPER.length; i++) if (db <= TAPER[i][0]) { const [d0, p0] = TAPER[i - 1], [d1, p1] = TAPER[i]; return p0 + ((db - d0) / (d1 - d0)) * (p1 - p0); }
  return 1;
}
export function posToDb(pos: number) {
  pos = Math.max(0, Math.min(1, pos));
  for (let i = 1; i < TAPER.length; i++) if (pos <= TAPER[i][1]) { const [d0, p0] = TAPER[i - 1], [d1, p1] = TAPER[i]; return d0 + ((pos - p0) / (p1 - p0)) * (d1 - d0); }
  return 6;
}
export const FADER_MARKS = [6, 0, -5, -10, -20, -30, -40, -60];
export const fmtDb = (db: number) => (db <= -95.9 ? '−∞' : `${db > 0 ? '+' : db < 0 ? '−' : ''}${Math.abs(db).toFixed(1).replace('.', ',')}`);
